import React, { useEffect, useState } from "react";
import Header from "../../Header/Header";
import CustomTitle from "../../Custom/CustomTitle";
import CustomTable from "../../../components/common/CustomTable";
import CustomSearch from "../../Custom/CustomSearch";
import CustomButton from "../../../components/common/CustomButton";
import CustomModel from "../../../components/common/CustomModel";
import teamService from "../../../components/Services/TeamService";
import CustomSelect from "../../../components/common/CustomSelect";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/Constant/Notification";
import teamServices from "../../Services/teamServices";
import { EditIcon } from "lucide-react";
import { ToastContainer } from "react-toastify";

const TeamPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [loginTime, setLoginTime] = useState("");
  const [logoutTime, setLogoutTime] = useState("");

  const [nextId, setNextId] = useState("");
  const [previousId, setPreviousId] = useState("");
  const [teamMaxCount, setTeamMaxCount] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);

  const [teamCache, setTeamCache] = useState({});

  const [searchQuery, setSearchQuery] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [isConflict, setIsConflict] = useState(false);
  const [conflictData, setConflictData] = useState([]);

  const columns = [
    { key: "name", title: "Team Name" },
    { key: "teamLead", title: "Team Lead" },
    { key: "members", title: "Team Members" },
    { key: "workingTime", title: "Working Hour" },
    {
      key: "Action",
      title: "Action",
      render: (value, row) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="text-blue-500 hover:underline"
            onClick={() => handleEditData(row)}
          >
            <EditIcon className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  const handleEditData = async (row) => {
    setIsEditing(true);
    try {
      const response = await teamServices.getGeoLocationTeamId(row.id);
      const team = response.data;

      setTeamName(team.name);
      setIsOpen(true);
      setEditingTeam(row);
      setSelectedLeader({
        value: team.teamLead.id,
        label: team.teamLead.name,
      });

      setSelectedMembers(
        team.members?.map((member) => ({
          value: member.id,
          label: member.name,
        })) || []
      );
      setLoginTime(team.loginTime);
      setLogoutTime(team.logoutTime);
    } catch (error) {
      console.error("Error fetching team data for edit:", error);
    }
  };

  const fetchTeams = async (
    pageToken = "",
    direction = "",
    page = 1,
    useCache = true,
    searchKey = ""
  ) => {
    const actualSearchKey = searchKey || searchQuery;
    const cacheKey = `page_${page}_limit_${pageLimit}_search_${actualSearchKey}`;

    if (useCache && teamCache[cacheKey]) {
      const cachedData = teamCache[cacheKey];
      setTeamData(cachedData.teams);
      setNextId(cachedData.next);
      setPreviousId(cachedData.prev);
      setTeamMaxCount(cachedData.maxCount);
      setCurrentPage(page);
      return;
    }

    setLoading(true);
    try {
      const body = {
        searchKey: actualSearchKey, // ✅ FIXED here
        limit: pageLimit,
        next: pageToken,
        direction: direction,
      };

      const response = await teamServices.getGeoLoactionTeams(body);
      const { teams, next, prev, maxCount } = response.data;

      setTeamData(teams);
      setNextId(next);
      setPreviousId(prev);
      setTeamMaxCount(maxCount);
      setCurrentPage(page);

      setTeamCache((prevCache) => ({
        ...prevCache,
        [cacheKey]: { teams, next, prev, maxCount },
      }));
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setTeamCache({});
    setCurrentPage(1);
    fetchTeams("", "", 1, false, searchQuery);
  };

  const handleTeamManagement = async () => {
    if (
      !teamName ||
      !selectedLeader ||
      selectedMembers.length === 0 ||
      !loginTime ||
      !logoutTime
    ) {
      showErrorToast("Please fill all fields");
      return;
    }

    const isMemberSelected = selectedMembers.some(
      (member) => member.value === selectedLeader.value
    );
    if (isMemberSelected) {
      showErrorToast("Team lead cannot be a team member");
      return;
    }

    setLoading(true);
    try {
      const teamData = {
        name: teamName,
        TeamLead: selectedLeader.value,
        members: selectedMembers.map((member) => member.value),
        loginTime,
        logoutTime,
      };

      let response;
      if (isEditing && editingTeam) {
        response = await teamServices.updateGeoLocationTeam(
          editingTeam.id,
          teamData
        );
        
        // Handle update response (200 status)
        if (response.status === 200) {
          // Check for conflicts in update response
          if (response.data.conflicts && response.data.conflicts.conflictUsers && response.data.conflicts.conflictUsers.length > 0) {
            setIsConflict(true);
            setConflictData(response.data.conflicts.conflictUsers || []);
            showSuccessToast(response.data?.message || "Team updated successfully with some conflicts");
          } else {
            setIsConflict(false);
            showSuccessToast(response.data?.message || "Team updated successfully");
          }
        }
      } else {
        response = await teamServices.createGeoLocationTeam(teamData);
        
        if (response.status === 201) {
          // Team created successfully, check for conflicts
          if (response.data.conflicts && response.data.conflicts.conflictUsers && response.data.conflicts.conflictUsers.length > 0) {
            setIsConflict(true);
            setConflictData(response.data.conflicts.conflictUsers || []);
            showSuccessToast(response.data?.message || "Team created successfully with some conflicts");
          } else {
            setIsConflict(false);
            showSuccessToast(response.data?.message || "Team created successfully");
          }
        }
        
        if (response.status === 200) {
          setIsConflict(false);
          showSuccessToast(
            response.data?.message || "Team created successfully"
          );    
        }
      }

      setTeamCache({});
      setIsOpen(false);
      setIsEditing(false);
      setEditingTeam(null);
      setTeamName("");
      setSelectedLeader(null);
      setSelectedMembers([]);
      setLoginTime("");
      setLogoutTime("");

      fetchTeams("", "", currentPage, false);
    } catch (error) {
      console.error("Error managing team:", error);
      if (error.response?.status === 409) {
        // Handle full conflict scenario
        setIsConflict(true);
        
        // Check different possible conflict data structures in the error response
        let conflictUsers = [];
        if (error.response.data.conflicts) {
          conflictUsers = error.response.data.conflicts;
        } else if (error.response.data.allConflicts) {
          conflictUsers = error.response.data.allConflicts;
        } else if (error.response.data.conflictUser) {
          conflictUsers = [error.response.data.conflictUser];
        }
        
        setConflictData(conflictUsers);
        showErrorToast(error.response.data.message || "Cannot create team due to conflicts");
      } else {
        showErrorToast(error.response?.data?.message || "An error occurred while managing the team");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (token, direction) => {
    console.log("Page change token:", token, "Direction:", direction);

    if (direction === "next") {
      fetchTeams(token, "next", currentPage + 1, true);
    } else if (direction === "previous") {
      fetchTeams(token, "previous", currentPage - 1, true);
    }
  };

  const handlePageSize = (size) => {
    setPageLimit(size);
    setCurrentPage(1);
    setTeamCache({});
    fetchTeams("", "", 1, false);
  };

  useEffect(() => {
    fetchTeams("", "", 1, false);
  }, [pageLimit]);

  const fetchUserData = async () => {
    try {
      const response = await teamService.getUserName();
      setUserData(response.data.users);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <div>
      <Header />
      <ToastContainer/>
      <div className="px-6 pt-28 pb-10">
        <div className="flex justify-between items-center mb-8">
          <CustomTitle
            title={`Teams (${teamMaxCount})`}
            isIcon={true}
            textColor={"primary"}
          />

          <div className="flex items-center gap-4">
            <CustomSearch
              placeholder={"Search Teams"}
              handleSearchChange={handleSearchChange}
              searchTerm={searchQuery}
              handleSearchSubmit={handleSearchSubmit}
            />
            <CustomButton
              label={"Add Team"}
              onClick={() => {
                setIsEditing(false);
                setEditingTeam(null);
                setTeamName("");
                setSelectedLeader(null);
                setSelectedMembers([]);
                setLoginTime("");
                setLogoutTime("");
                setIsOpen(true);
              }}
            />
          </div>
        </div>

        <div className="mt-8">
          <CustomTable
            data={teamData}
            columns={columns}
            isPagination={true}
            pageSize={pageLimit}
            currentPage={currentPage}
            nextId={nextId}
            prevId={previousId}
            hasMore={!!nextId}
            loading={loading}
            onPageSizeChange={handlePageSize}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <CustomModel
        title={isEditing ? "Edit Team" : "Add Team"}
        isOpen={isOpen}
        isClose={() => {
          setIsOpen(false);
          setIsEditing(false);
          setEditingTeam(null);
        }}
        content={
          <div className="flex flex-col gap-4">
            <label> Team Name</label>
            <input
              type="text"
              placeholder="Team Name"
              className="border rounded-lg p-2 input-focus "
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
            />
            <label>Leader</label>
            <CustomSelect
              options={userData.map((user) => ({
                value: user.id,
                label: user.name,
              }))}
              placeholder={"Select Leader"}
              onChange={(selectedOption) => setSelectedLeader(selectedOption)}
              value={selectedLeader}
            />

            <label>Members</label>
            <CustomSelect
              options={userData.map((user) => ({
                value: user.id,
                label: user.name,
              }))}
              placeholder="Select Members"
              isMulti={true}
              onChange={(selectedOption) => setSelectedMembers(selectedOption)}
              value={selectedMembers}
            />
            <span className="text-customGreen font-medium">Working Timing</span>
            <div className="flex gap-4">
              <div className="flex flex-col gap-2 w-full">
                <label>Login Time</label>
                <input
                  type="time"
                  value={loginTime}
                  onChange={(e) => setLoginTime(e.target.value)}
                  className="border rounded-lg p-2 w-full"
                />
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label>Logout Time</label>
                <input
                  type="time"
                  value={logoutTime}
                  onChange={(e) => setLogoutTime(e.target.value)}
                  className="border rounded-lg p-2 w-full"
                />
              </div>
            </div>
          </div>
        }
        onConfirm={handleTeamManagement}
        onConfirmText={isEditing ? "Update Team" : "Add Team"}
        isLoading={loading}
      />

      {isConflict && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg w-1/2">
            <div className="flex bg-slate-100 p-2 rounded-md justify-between items-center mb-6">
              <div
                id="bulk-upload-title"
                className="text-lg font-medium text-green-700"
              >
                <div className="bg- cursor-pointer flex items-center justify-center gap-2 text-base text-customGreen font-medium s w-full rounded-[10px] p-2">
                  <div>{"Conflicted Team"}</div>
                </div>
              </div>
              <button
                onClick={() => setIsConflict(false)}
                className="text-red-600 bg-white px-2 focus:outline-none  rounded-full p-1"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div>
                
                <ul className="list-disc pl-5">
                    {conflictData.map((conflict, index) => (
                    <li key={index} className="text-gray-700">
                        {conflict.userName}  - {conflict.existingTeam} 
                    </li>
                    ))}
                </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
